import { Link } from 'react-router-dom';

const services = [
  {
    slug: 'web-development',
    icon: 'ri-global-line',
    title: 'Web Development',
    description: 'We offer end-to-end web development services tailored to your business goals, ensuring speed, security, and scalability.',
    features: ['Business & corporate websites', 'Startup & enterprise solutions', 'Scalable architecture', 'Secure coding standards'],
  },
  {
    slug: 'mobile-app-development',
    icon: 'ri-smartphone-line',
    title: 'Mobile App Development',
    description: 'In a mobile-first world, having a smart, scalable, and user-friendly mobile app is essential for business growth.',
    features: ['Native Android development', 'Secure and scalable app', 'Google Play Store deployment', 'Performance optimization'],
  },
  {
    slug: 'digital-marketing',
    icon: 'ri-megaphone-line',
    title: 'Digital Marketing',
    description: "In today's digital-first world, your business needs more than just an online presence—it needs visibility, engagement, and conversions.",
    features: ['Increase brand awareness and credibility', 'Generate qualified leads and sales', 'Cost-effective compared to marketing'],
  },
  {
    slug: 'ui-ux-design',
    icon: 'ri-palette-line',
    title: 'UI/UX Design',
    description: 'Create stunning, user-centric designs that delight your users and drive engagement across all platforms.',
    features: ['User research & testing', 'Wireframing & prototyping', 'Visual design systems', 'Interaction design'],
  },
  {
    slug: 'cloud-solutions',
    icon: 'ri-cloud-line',
    title: 'Cloud Solutions',
    description: 'Leverage the power of cloud computing to scale your business operations and reduce infrastructure costs.',
    features: ['Cloud migration services', 'AWS/Azure/GCP expertise', 'Serverless architecture', 'DevOps automation'],
  },
  {
    slug: 'it-consulting',
    icon: 'ri-customer-service-line',
    title: 'IT Consulting',
    description: 'Strategic technology consulting to help you make informed decisions and achieve your business objectives.',
    features: ['Technology roadmap planning', 'Digital transformation', 'Process optimization', 'Risk assessment'],
  },
];

export { services };

const ServicesSection = () => {
  return (
    <section id="services" className="relative w-full bg-background-50 border-t border-background-200 py-14 md:py-16">
      <div className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                  <i className="ri-archive-2-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">
                  02 / Services
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.02] tracking-tight text-foreground-900 max-w-3xl">
                Six services.
                <span className="block text-primary-500">One obsession.</span>
              </h2>
            </div>
            <p className="max-w-md text-[15px] leading-relaxed text-foreground-700">
              Each service is built with enterprise-grade standards and obsessive attention to detail. Pick your need — we already handled the complexity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="group relative bg-background-100 rounded-2xl p-7 md:p-8 border border-background-200 transition-all duration-500 hover:-translate-y-1 hover:border-foreground-900/30"
              >
                {/* Corner brackets on hover */}
                <div className="absolute top-3 left-3 w-4 h-4 border-l-2 border-t-2 border-foreground-900 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="absolute top-3 right-3 w-4 h-4 border-r-2 border-t-2 border-foreground-900 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="absolute bottom-3 left-3 w-4 h-4 border-l-2 border-b-2 border-foreground-900 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="absolute bottom-3 right-3 w-4 h-4 border-r-2 border-b-2 border-foreground-900 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-foreground-900 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${service.icon} text-xl text-background-50`}></i>
                  </div>

                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-500 mb-2 block">
                    {String(index + 1).padStart(2, '0')} / Service
                  </span>

                  <h3 className="font-display text-xl font-bold text-foreground-900 mb-3 group-hover:text-primary-500 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-5">
                    {service.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2 text-sm text-foreground-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 flex-shrink-0"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/services/${service.slug}`}
                    className="inline-flex items-center gap-2 text-primary-500 font-medium text-sm group/link"
                  >
                    Read More
                    <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;